// Helper function to get the correct API base URL based on current subdomain
const getApiBaseUrl = () => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const port = window.location.port;
  
  console.log('🔍 API URL Detection:', { hostname, protocol, port });
  // Local testing: ALWAYS use local proxy, NEVER touch production
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0') {
    return '/api';
  }
  
  // For subdomain-based environments
  if (hostname.includes('stockwise-dev.')) {
    const url = `${protocol}//${hostname}${port ? ':' + port : ''}/api`;
    console.log('🔧 Development API URL:', url);
    return url;
  }
  if (hostname.includes('stockwise-staging.')) {
    const url = `${protocol}//${hostname}${port ? ':' + port : ''}/api`;
    console.log('🎭 Staging API URL:', url);
    return url;
  }
  
  // Production - check for stockwise-prod subdomain first
  if (hostname.includes('stockwise-prod.')) {
    const url = `${protocol}//${hostname}${port ? ':' + port : ''}/api`;
    console.log('📱 Production API URL (subdomain):', url);
    return url;
  }
  
  // Fallback for production or localhost
  const baseUrl = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || `${protocol}//${hostname}${port ? ':' + port : ''}/api`;
  console.log('🌐 Fallback API URL:', baseUrl);
  return baseUrl;
};

const API_BASE_URL = getApiBaseUrl();

// Helper function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('token');
};

// Helper function for authenticated requests
const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
  const token = getAuthToken();
  
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // On 401, clear session and redirect to login — stops React Query retry loops.
  // Only clear when the rejected token is still the current one: a slow request
  // sent with a previous session must not wipe a token the user just obtained.
  if (response.status === 401) {
    if (getAuthToken() === token) {
      localStorage.removeItem('token');
      localStorage.removeItem('stockwise-user-cache');
      window.dispatchEvent(new CustomEvent('authStateChange', { detail: false }));
    }
    const error: any = new Error('Unauthorized');
    error.status = 401;
    throw error;
  }

  return response;
};

// ── Media uploads (product photos + product videos) ─────────────────────
// Multipart with bearer auth. No Content-Type header is set on purpose: the
// browser must add the multipart boundary itself.
export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
export const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-m4v'];
export const MAX_IMAGE_UPLOAD_MB = 5;
export const MAX_VIDEO_UPLOAD_MB = 250;

async function mediaUpload(endpoint: 'image' | 'video', field: 'image' | 'video', file: File): Promise<string> {
  const token = getAuthToken();
  const formData = new FormData();
  formData.append(field, file);

  const response = await fetch(`${API_BASE_URL}/v1/upload/${endpoint}`, {
    method: 'POST',
    ...(token && { headers: { Authorization: `Bearer ${token}` } }),
    body: formData,
  });
  if (!response.ok) {
    let detail = '';
    try {
      detail = (await response.json()).detail || '';
    } catch {
      // non-JSON error body (proxy/HTML) — fall through to generic message
    }
    throw new Error(detail || `Failed to upload ${endpoint}`);
  }
  const { url } = await response.json();
  return url;
}

export const mediaAPI = {
  uploadImage: (file: File) => mediaUpload('image', 'image', file),
  uploadVideo: (file: File) => mediaUpload('video', 'video', file),
};

// Auth API
export const authAPI = {
  login: async (email: string, password: string) => {
    const response = await fetch(`${API_BASE_URL}/v1/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email,
        password: password,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Login failed');
    }

    return response.json();
  },

  register: async (userData: {
    email: string;
    password: string;
    fullName: string;
    companyName: string;
    phoneNumber?: string;
    companyType?: string;
    referralCode?: string;
  }) => {
    const response = await fetch(`${API_BASE_URL}/v1/auth/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: userData.email,
        password: userData.password,
        full_name: userData.fullName,
        company_name: userData.companyName,
        company_phone: userData.phoneNumber || '',
        company_type: userData.companyType || 'general',
        // Carried from the referral signup link; validated server-side.
        referral_code: userData.referralCode || undefined,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Registration failed');
    }

    return response.json();
  },

  // Alias for register to match frontend expectations
  signup: async (userData: {
    email: string;
    password: string;
    fullName: string;
    companyName: string;
    phoneNumber?: string;
    companyType?: string;
    referralCode?: string;
  }) => {
    return authAPI.register(userData);
  },

  getCurrentUser: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/auth/me`);
    if (!response.ok) {
      throw new Error('Failed to get current user');
    }
    return response.json();
  },

  forgotPassword: async (email: string) => {
    const response = await fetch(`${API_BASE_URL}/v1/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to send reset email');
    }

    return response.json();
  },

  resetPassword: async (token: string, email: string, newPassword: string) => {
    console.log("resetPassword called with:", { token, email, newPassword });
    const payload = { token, email, new_password: newPassword };
    console.log("Sending payload:", payload);
    const response = await fetch(`${API_BASE_URL}/v1/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = typeof errorData.detail === 'object' 
        ? JSON.stringify(errorData.detail) 
        : (errorData.detail || 'Failed to reset password');
      throw new Error(errorMessage);
    }

    return response.json();
  },

  createCreditCustomer: async (customerData: {
    name: string;
    phone?: string;
    email?: string;
    credit_limit?: number;
    location?: string;
    company_id: number;
  }) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/credit/customers/`, {
      method: 'POST',
      body: JSON.stringify(customerData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to create credit customer');
    }
    return response.json();
  },

  getCurrentCompanyId: (): number | null => {
    try {
      const cached = localStorage.getItem('stockwise-user-cache');
      if (cached) {
        const user = JSON.parse(cached);
        return user?.company_id ?? null;
      }
    } catch {
      // ignore
    }
    return null;
  },
};

// Dashboard API — aggregates from existing endpoints
export const dashboardAPI = {
  getStats: async () => {
    // Build stats from existing working endpoints
    const [inventoryRes, salesRes, usersRes] = await Promise.allSettled([
      fetchWithAuth(`${API_BASE_URL}/v1/inventory/analytics/`),
      fetchWithAuth(`${API_BASE_URL}/v1/orders/analytics/sales?period=all_time`),
      fetchWithAuth(`${API_BASE_URL}/v1/admin/users`),
    ]);

    const inventory = inventoryRes.status === 'fulfilled' && inventoryRes.value.ok
      ? await inventoryRes.value.json() : {};
    const sales = salesRes.status === 'fulfilled' && salesRes.value.ok
      ? await salesRes.value.json() : {};
    const users = usersRes.status === 'fulfilled' && usersRes.value.ok
      ? await usersRes.value.json() : [];

    return {
      total_users: Array.isArray(users) ? users.length : 0,
      active_users: Array.isArray(users) ? users.filter((u: any) => u.is_active).length : 0,
      total_products: inventory.total_products ?? 0,
      total_orders: sales.total_orders ?? 0,
      total_customers: 0,
      total_branches: 0,
      recent_signups: 0,
      total_revenue: sales.total_revenue ?? 0,
      monthly_revenue: 0,
      recent_activities: [],
    };
  },

  getRecentActivities: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/orders/?order_type=sales&limit=10`);
    if (!response.ok) return [];
    return response.json();
  },

  getInventoryAlerts: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/inventory/products/?status=low_stock`);
    if (!response.ok) return [];
    return response.json();
  },
};

// Companies API
export const companiesAPI = {
  // Get own company details (GET /v1/companies/{id} — members can access their
  // own company; the /system-admin variant is admin-only)
  getCompany: async (companyId: number) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/companies/${companyId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch company details');
    }
    return response.json();
  },

  getCompanies: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/companies/`);
    if (!response.ok) {
      throw new Error('Failed to fetch companies');
    }
    return response.json();
  },

  createCompany: async (companyData: {
    name: string;
    description?: string;
    address?: string;
    phone?: string;
    email?: string;
  }) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/companies/`, {
      method: 'POST',
      body: JSON.stringify(companyData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to create company');
    }
    return response.json();
  },

  updateCompany: async (companyId: number, companyData: any) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/companies/${companyId}`, {
      method: 'PUT',
      body: JSON.stringify(companyData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to update company');
    }
    return response.json();
  },

  deleteCompany: async (companyId: number) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/companies/${companyId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error('Failed to delete company');
    }
    return response.json();
  },
};

// Users API
export const usersAPI = {
  getUsers: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/users/`);
    if (!response.ok) {
      throw new Error('Failed to fetch users');
    }
    return response.json();
  },

  createUser: async (userData: {
    email: string;
    password: string;
    full_name: string;
    role: string;
    company_id?: number;
    phone?: string;
  }) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/users/`, {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to create user');
    }
    return response.json();
  },

  updateUser: async (userId: number, userData: any) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to update user');
    }
    return response.json();
  },

  deleteUser: async (userId: number) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/users/${userId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error('Failed to delete user');
    }
    return response.json();
  },
};

// Branches API
export const branchesAPI = {
  getBranches: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/branches/`);
    if (!response.ok) {
      throw new Error('Failed to fetch branches');
    }
    return response.json();
  },

  createBranch: async (branchData: {
    name: string;
    location?: string;
    manager_name?: string;
    contact_phone?: string;
    is_active?: boolean;
  }) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/branches/`, {
      method: 'POST',
      body: JSON.stringify(branchData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to create branch');
    }
    return response.json();
  },

  updateBranch: async (branchId: number, branchData: any) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/branches/${branchId}`, {
      method: 'PUT',
      body: JSON.stringify(branchData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to update branch');
    }
    return response.json();
  },

  deleteBranch: async (branchId: number) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/branches/${branchId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error('Failed to delete branch');
    }
    return response.json();
  },
};

// Inventory API
export const inventoryAPI = {
  getProducts: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/inventory/products/`);
    if (!response.ok) {
      throw new Error('Failed to fetch products');
    }
    return response.json();
  },

  createProduct: async (productData: {
    name: string;
    description?: string;
    sku: string;
    purchase_price?: number;
    sales_price: number;
    quantity: number;
    reorder_level?: number;
    category?: string;
    supplier_id?: number;
    image?: string;
    public_images?: string;
    public_videos?: string;
  }) => {
    // If image is provided, also set it as public_images for storefront
    if (productData.image && !productData.public_images) {
      productData.public_images = productData.image;
    }
    
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/inventory/products/`, {
      method: 'POST',
      body: JSON.stringify(productData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to create product');
    }
    return response.json();
  },

  updateProduct: async (productId: string, productData: any) => {
    // If image is provided, also set it as public_images for storefront
    if (productData.image && !productData.public_images) {
      productData.public_images = productData.image;
    }
    
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/inventory/products/${productId}`, {
      method: 'PUT',
      body: JSON.stringify(productData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to update product');
    }
    return response.json();
  },

  getProductHistory: async (params?: { start_date?: string; end_date?: string; limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.start_date) searchParams.append('start_date', params.start_date);
    if (params?.end_date) searchParams.append('end_date', params.end_date);
    if (params?.limit) searchParams.append('limit', params.limit.toString());

    const response = await fetchWithAuth(`${API_BASE_URL}/v1/inventory/history?${searchParams}`);
    if (!response.ok) {
      throw new Error('Failed to fetch product history');
    }
    return response.json();
  },

  deleteProduct: async (productId: number) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/inventory/products/${productId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error('Failed to delete product');
    }
    return response.json();
  },

  getAnalytics: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/inventory/analytics/`);
    if (!response.ok) {
      throw new Error('Failed to fetch inventory analytics');
    }
    return response.json();
  },

  // Stock movements
  recordStockMovement: async (movementData: {
    product_id: number;
    movement_type: 'in' | 'out' | 'adjustment';
    quantity: number;
    reason?: string;
    reference_number?: string;
  }) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/inventory/stock-movements/`, {
      method: 'POST',
      body: JSON.stringify(movementData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to record stock movement');
    }
    return response.json();
  },

  getStockMovements: async (params?: { 
    product_id?: number; 
    start_date?: string; 
    end_date?: string; 
    limit?: number 
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.product_id) searchParams.append('product_id', params.product_id.toString());
    if (params?.start_date) searchParams.append('start_date', params.start_date);
    if (params?.end_date) searchParams.append('end_date', params.end_date);
    if (params?.limit) searchParams.append('limit', params.limit.toString());

    const response = await fetchWithAuth(`${API_BASE_URL}/v1/inventory/stock-movements?${searchParams}`);
    if (!response.ok) {
      throw new Error('Failed to fetch stock movements');
    }
    return response.json();
  },
};

// Suppliers API
export const suppliersAPI = {
  getSuppliers: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/suppliers/`);
    if (!response.ok) {
      throw new Error('Failed to fetch suppliers');
    }
    return response.json();
  },

  // Products linked to a supplier (used by the purchase-order picker).
  getProductsBySupplier: async (supplierId: number) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/suppliers/${supplierId}/products`);
    if (!response.ok) {
      throw new Error('Failed to fetch products for supplier');
    }
    return response.json();
  },

  createSupplier: async (supplierData: {
    name: string;
    contact_person?: string;
    email?: string;
    phone?: string;
    address?: string;
    company_id: number;
  }) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/suppliers/`, {
      method: 'POST',
      body: JSON.stringify(supplierData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to create supplier');
    }
    return response.json();
  },

  updateSupplier: async (supplierId: number, supplierData: any) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/suppliers/${supplierId}`, {
      method: 'PUT',
      body: JSON.stringify(supplierData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to update supplier');
    }
    return response.json();
  },

  deleteSupplier: async (supplierId: number) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/suppliers/${supplierId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error('Failed to delete supplier');
    }
    return response.json();
  },
};

// One person in the customer directory: a saved customer record merged with
// whatever the storefront checkout captured (name, email, phone, address).
export interface CustomerDirectoryEntry {
  id: string;
  name: string;
  email: string | null;
  phone_number: string | null;
  address: string | null;
  orders_count: number;
  total_spent: number;
  last_order_at: string | null;
  sources: string[];
}

// Customers API
export const customersAPI = {
  getDirectory: async (): Promise<CustomerDirectoryEntry[]> => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/customers/directory`);
    if (!response.ok) {
      throw new Error('Failed to fetch customers');
    }
    return response.json();
  },

  createCustomer: async (customer: {
    name: string;
    email?: string;
    phone_number?: string;
    address?: string;
  }) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/customers/`, {
      method: 'POST',
      body: JSON.stringify(customer),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to add customer');
    }
    return response.json();
  },
};

// Orders API
export const ordersAPI = {
  getOrders: async (orderType?: string) => {
    const url = orderType ? `${API_BASE_URL}/v1/orders/?order_type=${orderType}` : `${API_BASE_URL}/v1/orders/`;
    const response = await fetchWithAuth(url);
    if (!response.ok) {
      throw new Error('Failed to fetch orders');
    }
    return response.json();
  },

  getOrderDetails: async (orderId: number) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/orders/${orderId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch order details');
    }
    return response.json();
  },

  createOrder: async (orderData: {
    customer_name: string;
    customer_email?: string;
    customer_phone?: string;
    items: Array<{
      product_id: number;
      quantity: number;
      unit_price: number;
    }>;
    notes?: string;
  }) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/orders/`, {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to create order');
    }
    return response.json();
  },

  createSalesOrder: async (orderData: any) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/orders/`, {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to create sales order');
    }
    return response.json();
  },

  createPurchaseOrder: async (orderData: any) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/orders/`, {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to create purchase order');
    }
    return response.json();
  },

  updateOrder: async (orderId: number, orderData: any) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/orders/${orderId}`, {
      method: 'PUT',
      body: JSON.stringify(orderData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to update order');
    }
    return response.json();
  },

  deleteOrder: async (orderId: number) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/orders/${orderId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error('Failed to delete order');
    }
    return response.json();
  },

  getOrderAnalytics: async (orderType: string = "sales", params?: { period?: string }) => {
    const period = params?.period || "today";
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/orders/analytics/${orderType}?period=${period}`);
    if (!response.ok) {
      throw new Error('Failed to fetch order analytics');
    }
    return response.json();
  },

  getPurchaseAnalytics: async (params?: { period?: string }) => {
    const period = params?.period || "today";
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/orders/analytics/purchases?period=${period}`);
    if (!response.ok) {
      throw new Error('Failed to fetch purchase analytics');
    }
    return response.json();
  },

  /**
   * Deep dashboard analytics: period-over-period comparisons, gross margin
   * with cost coverage, top products by revenue share, credit exposure and
   * restock estimate — one request for the whole insights panel.
   */
  getInsights: async (params?: { period?: string }) => {
    const period = params?.period || "today";
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/analytics/insights?period=${period}`);
    if (!response.ok) {
      throw new Error('Failed to fetch analytics insights');
    }
    return response.json();
  },

  getCreditCustomers: async (params?: { search?: string }) => {
    const url = new URL(`${API_BASE_URL}/v1/credit/customers/`);
    if (params?.search) url.searchParams.set('search', params.search);
    const response = await fetchWithAuth(url.toString());
    if (!response.ok) throw new Error('Failed to fetch credit customers');
    return response.json();
  },

  getCreditCustomer: async (id: number) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/credit/customers/${id}`);
    if (!response.ok) throw new Error('Failed to fetch credit customer');
    return response.json();
  },

  getCustomerPayments: async (customerId: number) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/credit/customers/${customerId}/payments`);
    if (!response.ok) throw new Error('Failed to fetch payments');
    return response.json();
  },

  getOrdersForCreditCustomer: async (customerId: number) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/orders/?credit_customer_id=${customerId}`);
    if (!response.ok) throw new Error('Failed to fetch orders');
    return response.json();
  },

  addCreditPayment: async (data: { credit_customer_id: number; amount: number; notes?: string }) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/credit/payments/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.detail || 'Failed to add payment');
    }
    return response.json();
  },

  updateCreditCustomer: async (id: number, data: any) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/credit/customers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to update credit customer');
    }
    return response.json();
  },
};

// Timber API
export const timberAPI = {
  // Timber categories
  getTimberCategories: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/timber/categories/`);
    if (!response.ok) {
      throw new Error('Failed to fetch timber categories');
    }
    return response.json();
  },

  createTimberCategory: async (categoryData: {
    name: string;
    description?: string;
  }) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/timber/categories/`, {
      method: 'POST',
      body: JSON.stringify(categoryData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to create timber category');
    }
    return response.json();
  },

  // Timber species
  getTimberSpecies: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/timber/species/`);
    if (!response.ok) {
      throw new Error('Failed to fetch timber species');
    }
    return response.json();
  },

  createTimberSpecies: async (speciesData: {
    name: string;
    scientific_name?: string;
    description?: string;
  }) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/timber/species/`, {
      method: 'POST',
      body: JSON.stringify(speciesData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to create timber species');
    }
    return response.json();
  },

  // Timber grades
  getTimberGrades: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/timber/grades/`);
    if (!response.ok) {
      throw new Error('Failed to fetch timber grades');
    }
    return response.json();
  },

  createTimberGrade: async (gradeData: {
    name: string;
    description?: string;
    quality_standard?: string;
  }) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/timber/grades/`, {
      method: 'POST',
      body: JSON.stringify(gradeData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to create timber grade');
    }
    return response.json();
  },

  // Production batches
  getProductionBatches: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/timber/production-batches/`);
    if (!response.ok) {
      throw new Error('Failed to fetch production batches');
    }
    return response.json();
  },

  createProductionBatch: async (batchData: {
    batch_number: string;
    production_date?: string;
    notes?: string;
  }) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/timber/production-batches/`, {
      method: 'POST',
      body: JSON.stringify(batchData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to create production batch');
    }
    return response.json();
  },

  // Timber analytics
  getTimberAnalytics: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/timber/analytics`);
    if (!response.ok) {
      throw new Error('Failed to fetch timber analytics');
    }
    return response.json();
  },

  // Timber inventory
  getTimberInventory: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/timber/inventory`);
    if (!response.ok) {
      throw new Error('Failed to fetch timber inventory');
    }
    return response.json();
  },
};

// System Admin API
export const systemAdminAPI = {
  // Get all companies (system admin only)
  getAllCompanies: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/system-admin/companies`);
    if (!response.ok) {
      throw new Error('Failed to fetch all companies');
    }
    return response.json();
  },

  // Get all users (system admin only) - Note: This endpoint doesn't exist in backend
  getAllUsers: async () => {
    // Since there's no global users endpoint, we'll return empty for now
    // You might want to implement this in the backend or get users per company
    return { users: [] };
  },

  // System statistics
  getSystemStats: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/system-admin/dashboard/stats`);
    if (!response.ok) {
      throw new Error('Failed to fetch system statistics');
    }
    return response.json();
  },

  // Update user role (system admin only) - Note: This endpoint doesn't exist in backend
  updateUserRole: async (userId: number, role: string) => {
    // This endpoint doesn't exist in the backend yet
    throw new Error('Update user role endpoint not implemented in backend');
  },

  // Deactivate/activate company
  updateCompanyStatus: async (companyId: number, isActive: boolean) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/system-admin/companies/${companyId}/toggle-active`, {
      method: 'PUT',
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to update company status');
    }
    return response.json();
  },

  // Get company details
  getCompanyDetails: async (companyId: number) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/system-admin/companies/${companyId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch company details');
    }
    return response.json();
  },

  // Get company users
  getCompanyUsers: async (companyId: number) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/system-admin/companies/${companyId}/users`);
    if (!response.ok) {
      throw new Error('Failed to fetch company users');
    }
    return response.json();
  },

  // Update company type
  updateCompanyType: async (companyId: number, newType: string) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/system-admin/companies/${companyId}/type`, {
      method: 'PUT',
      body: JSON.stringify({ new_type: newType }),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to update company type');
    }
    return response.json();
  },

  // Add company admin
  addCompanyAdmin: async (companyId: number, adminData: any) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/system-admin/companies/${companyId}/admin`, {
      method: 'POST',
      body: JSON.stringify(adminData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to add company admin');
    }
    return response.json();
  },
};

// Storefront API
export const storefrontAPI = {
  // Get storefront settings
  getStorefrontSettings: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/storefront/settings`);
    if (!response.ok) {
      throw new Error('Failed to fetch storefront settings');
    }
    return response.json();
  },

  // Create storefront settings
  createStorefrontSettings: async (settings: {
    store_name?: string;
    store_description?: string;
    logo_url?: string;
    theme_color?: string;
    layout_template?: string;
    contact_email?: string;
    contact_phone?: string;
    contact_whatsapp?: string;
    contact_address?: string;
    is_active?: boolean;
    custom_css?: string;
    store_slug?: string;
    meta_title?: string;
    meta_description?: string;
    social_facebook?: string;
    social_instagram?: string;
    social_twitter?: string;
    checkout_title?: string | null;
    checkout_subtitle?: string | null;
    checkout_color?: string | null;
    checkout_note?: string | null;
    checkout_show_logo?: boolean | null;
  }) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/storefront/settings`, {
      method: 'POST',
      body: JSON.stringify(settings),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to create storefront settings');
    }
    return response.json();
  },

  // Update storefront settings
  updateStorefrontSettings: async (settings: {
    store_name?: string;
    store_description?: string;
    logo_url?: string;
    theme_color?: string;
    layout_template?: string;
    contact_email?: string;
    contact_phone?: string;
    contact_whatsapp?: string;
    contact_address?: string;
    is_active?: boolean;
    custom_css?: string;
    store_slug?: string;
    meta_title?: string;
    meta_description?: string;
    social_facebook?: string;
    social_instagram?: string;
    social_twitter?: string;
    checkout_title?: string | null;
    checkout_subtitle?: string | null;
    checkout_color?: string | null;
    checkout_note?: string | null;
    checkout_show_logo?: boolean | null;
  }) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/storefront/settings`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to update storefront settings');
    }
    return response.json();
  },

  // Get storefront products
  getStorefrontProducts: async (publicOnly: boolean = false) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/storefront/products?public_only=${publicOnly}`);
    if (!response.ok) {
      throw new Error('Failed to fetch storefront products');
    }
    return response.json();
  },

  // Update product storefront settings
  updateProductStorefrontSettings: async (productId: number, settings: {
    is_public: boolean;
    public_price?: number;
    public_description?: string;
    public_images?: string;
    public_videos?: string;
    display_order?: number;
    featured?: boolean;
  }) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/storefront/products/${productId}`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to update product storefront settings');
    }
    return response.json();
  },

  // Get customer orders
  getCustomerOrders: async (statusFilter?: string, limit: number = 100, search?: string) => {
    const params = new URLSearchParams();
    if (statusFilter) params.append('status_filter', statusFilter);
    if (search && search.trim()) params.append('search', search.trim());
    params.append('limit', limit.toString());

    const response = await fetchWithAuth(`${API_BASE_URL}/v1/storefront/orders?${params}`);
    if (!response.ok) {
      throw new Error('Failed to fetch customer orders');
    }
    return response.json();
  },

  // Count of website orders still to fulfil — polled by the header badge so
  // new buyer orders chime without downloading the full (PII-bearing) list.
  getOpenOrderCount: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/storefront/orders/open-count`);
    if (!response.ok) {
      throw new Error('Failed to fetch open order count');
    }
    return response.json() as Promise<{ open: number }>;
  },

  // Update customer order
  updateCustomerOrder: async (orderId: number, update: {
    status?: string;
    admin_notes?: string;
    delivery_date?: string;
  }) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/storefront/orders/${orderId}`, {
      method: 'PUT',
      body: JSON.stringify(update),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to update customer order');
    }
    return response.json();
  },

  // Get storefront stats
  getStorefrontStats: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/storefront/stats`);
    if (!response.ok) {
      throw new Error('Failed to fetch storefront stats');
    }
    return response.json();
  },
};

// ── Web Push ────────────────────────────────────────────────────────────────
// Registers this device so the installed app can notify with no tab open.
export const pushAPI = {
  // The VAPID public key is unauthenticated by design: the browser needs it
  // before it can subscribe, and it is the half meant to be published.
  getVapidPublicKey: async () => {
    const response = await fetch(`${API_BASE_URL}/v1/push/vapid-public-key`);
    if (!response.ok) {
      throw new Error('Device notifications are not available on this server');
    }
    return response.json() as Promise<{ publicKey: string }>;
  },

  subscribe: async (subscription: {
    endpoint: string;
    keys: { p256dh: string; auth: string };
  }) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/push/subscribe`, {
      method: 'POST',
      body: JSON.stringify(subscription),
    });
    if (!response.ok) {
      // 409 means another account already owns this browser's subscription —
      // the caller replaces it with a fresh one (shared shop tablet). The
      // status is attached so the caller can tell that case apart.
      throw Object.assign(new Error('Failed to enable notifications'), {
        status: response.status,
      });
    }
    return response.json();
  },

  unsubscribe: async (endpoint: string) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/push/unsubscribe`, {
      method: 'DELETE',
      body: JSON.stringify({ endpoint }),
    });
    return response.ok;
  },
};

// Public API (no auth required)
export const publicAPI = {
  // Get public storefront
  getPublicStorefront: async (companyId: number) => {
    const response = await fetch(`${API_BASE_URL}/public/storefront/${companyId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch public storefront');
    }
    return response.json();
  },

  // Create customer order
  createCustomerOrder: async (companyId: number, orderData: {
    customer_name: string;
    customer_email?: string;
    customer_phone?: string;
    customer_address?: string;
    total_amount: number;
    notes?: string;
    customer_notes?: string;
    delivery_method?: string;
    delivery_address?: string;
    order_items: Array<{
      product_id: number;
      quantity: number;
      unit_price: number;
      total_price: number;
    }>;
  }) => {
    const response = await fetch(`${API_BASE_URL}/public/storefront/${companyId}/orders`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(orderData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to create customer order');
    }
    return response.json();
  },
};

// Export API_BASE_URL for other components
export { API_BASE_URL };

// Reminders API
export const remindersAPI = {
  sendReminder: async (customerId: number, message: string) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/reminders/send`, {
      method: 'POST',
      body: JSON.stringify({ customer_id: customerId, message }),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to send reminder');
    }
    return response.json();
  },
};

// Admin API
export const adminAPI = {
  // Dashboard stats
  getDashboardStats: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/admin/dashboard/stats`);
    if (!response.ok) {
      throw new Error('Failed to fetch dashboard stats');
    }
    return response.json();
  },

  // Branch management
  getBranches: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/admin/branches`);
    if (!response.ok) {
      throw new Error('Failed to fetch branches');
    }
    return response.json();
  },

  createBranch: async (branchData: {
    name: string;
    code: string;
    address?: string;
    phone_number?: string;
    manager_name?: string;
  }) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/admin/branches`, {
      method: 'POST',
      body: JSON.stringify(branchData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to create branch');
    }
    return response.json();
  },

  updateBranch: async (branchId: number, branchData: any) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/admin/branches/${branchId}`, {
      method: 'PUT',
      body: JSON.stringify(branchData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to update branch');
    }
    return response.json();
  },

  // User management
  getUsers: async (branchId?: number) => {
    const url = branchId 
      ? `${API_BASE_URL}/v1/admin/users?branch_id=${branchId}`
      : `${API_BASE_URL}/v1/admin/users`;
    const response = await fetchWithAuth(url);
    if (!response.ok) {
      throw new Error('Failed to fetch users');
    }
    return response.json();
  },

  createUser: async (userData: {
    email: string;
    full_name: string;
    phone_number?: string;
    role: string;
    branch_id?: number;
    send_invitation: boolean;
  }) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/admin/users`, {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to create user');
    }
    return response.json();
  },
};

// Production API (for liquid/manufacturing companies)
export const productionAPI = {
  // Formulas
  getFormulas: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/production/formulas`);
    if (!response.ok) throw new Error('Failed to fetch formulas');
    return response.json();
  },

  getFormula: async (id: number) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/production/formulas/${id}`);
    if (!response.ok) throw new Error('Failed to fetch formula');
    return response.json();
  },

  createFormula: async (data: {
    name: string;
    description?: string;
    output_quantity: number;
    output_unit: string;
    price_per_unit?: number;
    output_product_id?: number;
    ingredients: Array<{ product_id: number; quantity: number; unit: string }>;
  }) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/production/formulas`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.detail || 'Failed to create formula');
    }
    return response.json();
  },

  updateFormula: async (id: number, data: any) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/production/formulas/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.detail || 'Failed to update formula');
    }
    return response.json();
  },

  deleteFormula: async (id: number) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/production/formulas/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to delete formula');
    return response.json();
  },

  // Production runs
  previewRun: async (data: { formula_id: number; batches: number; actual_output?: number }) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/production/runs/preview`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.detail || 'Failed to preview run');
    }
    return response.json();
  },

  createRun: async (data: { formula_id: number; batches: number; actual_output?: number; notes?: string }) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/production/runs`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.detail || 'Failed to create production run');
    }
    return response.json();
  },

  getRuns: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/production/runs`);
    if (!response.ok) throw new Error('Failed to fetch production runs');
    return response.json();
  },
};

// Agent API
export const agentAPI = {
  getHistory: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/agent/history`);
    if (!response.ok) return { messages: [], summary: "", whatsapp_linked: false };
    return response.json();
  },

  clearSession: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/agent/session`, { method: "DELETE" });
    return response.json();
  },

  linkWhatsApp: async (phone: string) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/agent/whatsapp/link`, {
      method: "POST",
      body: JSON.stringify({ phone }),
    });
    return response.json();
  },

  // Returns a ReadableStream for SSE
  chat: (message: string): EventSource => {
    // We use fetch + ReadableStream for POST SSE
    return null as any; // handled in hook
  },
};

// Agent Bridge API
export const bridgeAPI = {
  getConnections: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/bridge/connections`);
    if (!response.ok) throw new Error('Failed to fetch agent connections');
    return response.json();
  },

  createConnection: async (data: any) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/bridge/connections`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.detail || 'Failed to create agent connection');
    }
    return response.json();
  },

  updateConnection: async (id: number, data: any) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/bridge/connections/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.detail || 'Failed to update agent connection');
    }
    return response.json();
  },

  deleteConnection: async (id: number) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/bridge/connections/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to revoke agent connection');
    return response.json();
  },

  getConnectionActions: async (id: number) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/v1/bridge/connections/${id}/actions`);
    if (!response.ok) throw new Error('Failed to fetch agent actions');
    return response.json();
  },

  approveAction: async (connectionId: number, actionId: number) => {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/v1/bridge/connections/${connectionId}/actions/${actionId}/approve`,
      { method: 'PATCH' }
    );
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.detail || 'Failed to approve action');
    }
    return response.json();
  },

  rejectAction: async (connectionId: number, actionId: number) => {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/v1/bridge/connections/${connectionId}/actions/${actionId}/reject`,
      { method: 'PATCH' }
    );
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.detail || 'Failed to reject action');
    }
    return response.json();
  },
};

export const API_BASE_URL_EXPORT = API_BASE_URL;
